// Übergabe für Hausmeister freigeben (Verwaltungsseite) — Ticket #436.
//
// Gleiches Bedienmuster wie der Hausmeister-Abschnitt im Ticket (TicketHausmeister.tsx):
// Ohne Freigabe sieht ein Hausmeister die Übergabe überhaupt nicht. Zusätzlich lässt sich
// hier eine Vorlage wählen — ein abgeschlossenes Protokoll derselben Einheit, aus dem der
// Assistent auf seinem Telefon Räume, Zähler und Schlüssel vorbelegt (Entscheidung Patrick:
// Vorlagen nur, wenn das Büro sie vorbereitet).
import React, { useCallback, useEffect, useState, type CSSProperties } from 'react';
import { CircleNotch, HardHat, Plus, X } from '@phosphor-icons/react';
import { BestaetigenDialog } from '../immobilien/ui';
import { ladeHausmeister } from '../../lib/hausmeister/api';
import {
  entzieheFreigabe, gibFrei, ladeVorlagenKandidaten, vorlagenLabel,
  type UebergabeFreigabe, type VorlagenKandidat,
} from '../../lib/uebergabe/hausmeisterFreigabe';

interface Props {
  protokollId: string;
  einheitId: string | null;
  freigaben: UebergabeFreigabe[];
  userId: string | null;
  onGeaendert: () => void;
}

const UebergabeHausmeister: React.FC<Props> = ({ protokollId, einheitId, freigaben, userId, onGeaendert }) => {
  const [hausmeister, setHausmeister] = useState<{ id: string; full_name: string | null; email: string | null }[]>([]);
  const [vorlagen, setVorlagen] = useState<VorlagenKandidat[]>([]);
  const [offen, setOffen] = useState(false);
  const [gewaehlt, setGewaehlt] = useState('');
  const [vorlage, setVorlage] = useState('');
  const [arbeitet, setArbeitet] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);
  const [entziehen, setEntziehen] = useState<UebergabeFreigabe | null>(null);

  useEffect(() => { void ladeHausmeister().then(setHausmeister); }, []);
  const oeffnen = useCallback(async () => {
    setOffen(true);
    setVorlagen(await ladeVorlagenKandidaten(einheitId, protokollId));
  }, [einheitId, protokollId]);

  // Ohne Hausmeister-Konten ist der Abschnitt nur Rauschen.
  if (hausmeister.length === 0 && freigaben.length === 0) return null;

  const name = (id: string) => {
    const h = hausmeister.find((x) => x.id === id);
    return h?.full_name || h?.email || 'Hausmeister';
  };
  const frei = hausmeister.filter((h) => !freigaben.some((f) => f.hausmeister_id === h.id));

  const freigeben = async () => {
    if (!gewaehlt) return;
    setArbeitet(true);
    const problem = await gibFrei({ protokollId, hausmeisterId: gewaehlt, vorlageId: vorlage || null, vonId: userId });
    setArbeitet(false);
    if (problem) { setFehler(problem); return; }
    setOffen(false); setGewaehlt(''); setVorlage(''); setFehler(null);
    onGeaendert();
  };

  const auswahl: CSSProperties = { height: 40, padding: '0 10px', borderRadius: 12, border: 'none', outline: 'none', background: 'var(--fill2)',
    color: 'var(--ink)', fontFamily: 'inherit', fontSize: 13, fontWeight: 600, minWidth: 0 };

  return (
    <div className="flex flex-col" style={{ gap: 8, padding: '10px 12px', borderRadius: 16, background: 'var(--fill)' }}>
      <div className="flex flex-wrap items-center" style={{ gap: 6 }}>
        <HardHat size={14} weight="fill" color="var(--ink3)" />
        <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--ink3)' }}>Hausmeister</span>
        {freigaben.map((f) => (
          <span key={f.id} className="inline-flex items-center" style={{ gap: 4, height: 26, padding: '0 4px 0 10px', borderRadius: 13, fontSize: 12, fontWeight: 700,
            background: 'var(--acc-soft)', color: 'var(--acc)' }}>
            {name(f.hausmeister_id)}{f.vorlage_id ? ' · mit Vorlage' : ''}
            <button type="button" aria-label="Freigabe entziehen" data-tip="Freigabe entziehen" onClick={() => setEntziehen(f)}
              className="inline-flex items-center justify-center" style={{ width: 20, height: 20, borderRadius: '50%', border: 'none', cursor: 'pointer', background: 'transparent', color: 'inherit' }}>
              <X size={11} weight="bold" />
            </button>
          </span>
        ))}
        {freigaben.length === 0 && !offen && <span style={{ fontSize: 12, color: 'var(--ink3)' }}>· nicht freigegeben</span>}
        {frei.length > 0 && !offen && (
          <button type="button" onClick={() => void oeffnen()} className="ml-auto inline-flex items-center"
            style={{ gap: 4, height: 32, padding: '0 12px', borderRadius: 16, border: 'none', cursor: 'pointer', fontFamily: 'inherit', fontSize: 12, fontWeight: 800,
              background: 'var(--acc-soft)', color: 'var(--acc)' }}>
            <Plus size={12} weight="bold" /> Freigeben
          </button>
        )}
      </div>

      {offen && (
        <div className="flex flex-wrap items-center" style={{ gap: 8 }}>
          <select value={gewaehlt} onChange={(e) => setGewaehlt(e.target.value)} aria-label="Hausmeister wählen" style={auswahl}>
            <option value="">Hausmeister wählen …</option>
            {frei.map((h) => <option key={h.id} value={h.id}>{h.full_name || h.email}</option>)}
          </select>
          <select value={vorlage} onChange={(e) => setVorlage(e.target.value)} aria-label="Vorlage"
            data-tip={vorlagen.length ? 'Räume, Zähler und Schlüssel aus einem früheren Protokoll derselben Einheit übernehmen' : 'Für diese Einheit gibt es kein abgeschlossenes Protokoll'}
            disabled={vorlagen.length === 0} style={{ ...auswahl, opacity: vorlagen.length === 0 ? 0.5 : 1 }}>
            <option value="">{vorlagen.length ? 'ohne Vorlage' : 'keine Vorlage vorhanden'}</option>
            {vorlagen.map((v) => <option key={v.id} value={v.id}>Vorlage: {vorlagenLabel(v)}</option>)}
          </select>
          <button type="button" onClick={() => void freigeben()} disabled={!gewaehlt || arbeitet} className="inline-flex items-center"
            style={{ gap: 6, height: 40, padding: '0 14px', borderRadius: 13, border: 'none', cursor: 'pointer', fontFamily: 'inherit', fontSize: 13, fontWeight: 800,
              background: 'var(--acc-fill)', color: 'var(--acc-ink)', boxShadow: 'var(--acc-sh)', opacity: !gewaehlt || arbeitet ? 0.45 : 1 }}>
            {arbeitet && <CircleNotch size={13} weight="bold" className="animate-spin" />} Freigeben
          </button>
          <button type="button" onClick={() => { setOffen(false); setFehler(null); }}
            style={{ height: 40, padding: '0 12px', borderRadius: 13, border: 'none', cursor: 'pointer', fontFamily: 'inherit', fontSize: 13, fontWeight: 700, background: 'var(--fill2)', color: 'var(--ink2)' }}>
            Abbrechen
          </button>
        </div>
      )}
      {fehler && <p className="m-0" role="alert" style={{ fontSize: 12, fontWeight: 700, color: 'var(--bad)' }}>{fehler}</p>}
      {entziehen && (
        <BestaetigenDialog titel="Freigabe entziehen?" icon={HardHat} bestaetigenLabel="Entziehen"
          text={`Freigabe für ${name(entziehen.hausmeister_id)} entziehen? Die Übergabe verschwindet dann aus seiner Ansicht.`}
          onAbbrechen={() => setEntziehen(null)}
          onBestaetigen={async () => { const f = entziehen; setEntziehen(null); await entzieheFreigabe(f.id); onGeaendert(); }} />
      )}
    </div>
  );
};

export default UebergabeHausmeister;
